/** Format an instant as a wall-clock value accepted by a datetime-local input. */
export function localDateTimeValue(
  iso: string,
  timezone = Intl.DateTimeFormat().resolvedOptions().timeZone,
): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(new Date(iso));
  const value = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((part) => part.type === type)?.value ?? "";
  return `${value("year")}-${value("month")}-${value("day")}T${value("hour")}:${
    value("minute")
  }`;
}

/** Resolve one timezone-local edit value to a real instant with explicit repeated-hour handling. */
export function zonedDateTimeToIso(
  value: string,
  timezone: string,
  disambiguation: "earlier" | "later" = "earlier",
  referenceIso?: string,
): string {
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/u.test(value)) {
    throw new Error("Invalid local date-time value");
  }
  if (referenceIso && localDateTimeValue(referenceIso, timezone) === value) {
    return new Date(referenceIso).toISOString();
  }
  const naive = Date.parse(`${value}:00.000Z`);
  const candidates: number[] = [];
  // IANA offsets are bounded to ±14 hours and may include quarter-hour increments.
  for (let offsetMinutes = -14 * 60; offsetMinutes <= 14 * 60; offsetMinutes += 15) {
    const candidate = naive - offsetMinutes * 60_000;
    if (localDateTimeValue(new Date(candidate).toISOString(), timezone) === value) {
      candidates.push(candidate);
    }
  }
  const distinct = [...new Set(candidates)].sort((left, right) => left - right);
  if (distinct.length === 0) throw new Error("The selected local time does not exist in this timezone");
  const selected = disambiguation === "later" ? distinct.at(-1) : distinct[0];
  if (selected === undefined) throw new Error("The selected local time could not be resolved");
  return new Date(selected).toISOString();
}
